import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { readableCurveError } from './errors';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { curveImportFieldHelp } from '../../app/guidance/fieldHelpRegistry';
import type { CurveLibraryApi } from './libraryApi';
import type { CurveLibraryDetail } from './libraryTypes';
import type { CurveImportRead } from './types';

interface CurveLibrarySubmissionPanelProps {
  api: CurveLibraryApi;
  imported: CurveImportRead;
  authenticated: boolean;
  disabled: boolean;
  onBusyChange?: ((busy: boolean) => void) | undefined;
}

export function CurveLibrarySubmissionPanel({ api, imported, authenticated, disabled, onBusyChange }: CurveLibrarySubmissionPanelProps) {
  const client = useQueryClient();
  const [manufacturer, setManufacturer] = useState(imported.suggestions.manufacturer ?? '');
  const [model, setModel] = useState(imported.suggestions.model ?? '');
  const [trim, setTrim] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState<CurveLibraryDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const submit = async () => {
    if (!authenticated || disabled || pending.current || !manufacturer.trim() || !model.trim() || imported.state !== 'verified') return;
    pending.current = true;
    setBusy(true);
    setError('');
    onBusyChange?.(true);
    try {
      setSubmitted(await api.submit({ import_id: imported.id, manufacturer: manufacturer.trim(), model: model.trim(), trim_label: trim.trim() || null, notes: notes.trim() || null }));
      await client.invalidateQueries({ queryKey: ['curve-library'] });
    } catch (cause: unknown) { setError(readableCurveError(cause)); }
    finally { pending.current = false; setBusy(false); onBusyChange?.(false); }
  };
  if (!authenticated || imported.state !== 'verified') return null;
  return <section aria-label="Submit to private reviewed library">
    <h3>Submit this saved configuration for master review</h3>
    <p>Saving a checked curve and approving a shared master are separate steps. This submits all saved RPM revisions for one trim; an authenticated curator must approve the bundle before it appears in the reviewed library.</p>
    {submitted === null ? <>
      <label>Manufacturer<input {...guided(curveImportFieldHelp.libraryManufacturer, 'submission')} aria-label="Submission manufacturer" maxLength={200} disabled={disabled || busy} value={manufacturer} onChange={(event) => setManufacturer(event.target.value)} /></label>
      <label>Model<input {...guided(curveImportFieldHelp.libraryModel, 'submission')} aria-label="Submission model" maxLength={200} disabled={disabled || busy} value={model} onChange={(event) => setModel(event.target.value)} /></label>
      <label>Trim to submit (required for mixed-trim sheets)<input {...guided(curveImportFieldHelp.libraryTrim, 'submission')} aria-label="Submission trim" maxLength={200} disabled={disabled || busy} value={trim} onChange={(event) => setTrim(event.target.value)} /></label>
      <label>Notes for the curator<textarea {...guided(curveImportFieldHelp.librarySubmissionNotes)} aria-label="Submission notes" maxLength={4000} disabled={disabled || busy} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
      <button disabled={disabled || busy || !manufacturer.trim() || !model.trim()} type="button" onClick={() => void submit()}>Submit configuration for review</button>
    </> : <p role="status">Library submission {submitted.entry.review_state}: {submitted.entry.tested_speeds_rpm.join(', ')} RPM. Entry <code>{submitted.entry.id}</code>. This does not replace the parent master or grant approval.</p>}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
